import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, CheckCircle2, Mail } from 'lucide-react';
import AppLogo from '@/components/AppLogo';
import SiteFooter from '@/components/SiteFooter';
import { AUTHOR } from '@/config/authorProfile';

const LAST_UPDATED = 'February 2, 2026';

// Corrections log — every material change gets a public entry here.
// Newest first. Format: { date, url, summary, resolution }.
const CORRECTIONS = [
  // Example entry (kept for the first real correction to replace):
  // {
  //   date: 'February 15, 2026',
  //   url: '/blog/tsa-precheck-vs-clear-vs-global-entry',
  //   summary: 'Reader noted TSA PreCheck fee updated from $78 to $77.95.',
  //   resolution: 'Updated in-line; re-dated article.',
  // },
];

export default function CorrectionsPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <header className="container mx-auto px-4 sm:px-6 py-6 max-w-4xl">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <AppLogo size={36} />
            <p className="heading text-sm font-semibold tracking-tight text-slate-900">
              How Busy Is My Airport
            </p>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 pb-16 max-w-3xl">
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="pt-6 pb-8"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600 mb-4">
            Corrections
          </p>
          <h1 className="heading text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-4">
            Corrections log
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
            When we get something wrong, we fix the article and record the
            change here. Airport policy, TSA rules, and product terms change
            often; when they do, this page is the honest ledger.
          </p>
          <p className="text-xs text-slate-500 mt-4">Last updated: {LAST_UPDATED}</p>
        </motion.section>

        {/* How to report */}
        <Card className="border-slate-200 bg-white shadow-sm mb-6">
          <CardContent className="p-6 sm:p-7">
            <h2 className="heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              How to report an error
            </h2>
            <ol className="list-decimal pl-5 space-y-2 text-sm text-slate-700 leading-relaxed">
              <li>
                Email{' '}
                <a href={`mailto:${AUTHOR.email}?subject=Correction`} className="text-blue-600 hover:underline">
                  {AUTHOR.email}
                </a>{' '}
                with <strong>"Correction"</strong> in the subject
              </li>
              <li>Include the article URL and the specific claim you believe is wrong</li>
              <li>Attach a source if you have one (screenshot, official link, dated quote)</li>
              <li>We reply {AUTHOR.responseTimePromise}</li>
              <li>Confirmed corrections are updated in-line and logged below with the date</li>
            </ol>
          </CardContent>
        </Card>

        {/* Log */}
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="p-6 sm:p-7">
            <h2 className="heading text-lg font-semibold text-slate-900 mb-4">
              Log of published corrections
            </h2>
            {CORRECTIONS.length === 0 ? (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-100" data-testid="corrections-empty">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-emerald-900">No corrections logged yet.</p>
                  <p className="text-xs text-emerald-800 mt-1">
                    This log starts on {LAST_UPDATED}. When we do log our first
                    correction, it will appear here — never buried or deleted.
                  </p>
                </div>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {CORRECTIONS.map((c, i) => (
                  <li key={i} className="py-4 first:pt-0 last:pb-0">
                    <p className="text-xs font-semibold text-slate-500 mb-1">{c.date}</p>
                    <p className="text-sm text-slate-800 mb-1">
                      <Link to={c.url} className="text-blue-600 hover:underline">{c.url}</Link>
                    </p>
                    <p className="text-sm text-slate-700 mb-1">{c.summary}</p>
                    <p className="text-xs text-slate-500 italic">Resolution: {c.resolution}</p>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-slate-500 mt-6 text-center">
          Our full editorial standards live at{' '}
          <Link to="/editorial-standards" className="text-blue-600 hover:underline">/editorial-standards</Link>.
        </p>
      </main>

      <SiteFooter />
    </div>
  );
}
